import { useSyncExternalStore } from "react";

// Base colors that don't change with theme
export const C_BASE = {
  orange: "#E8410A",
  orangeAlt: "#FF6B35",
  orangeLight: "#FFF5F0",
  peach: "#FDF0EA",
  gold: "#F5A623",
  green: "#22C55E",

  subject: {
    maths: "#2563EB",
    physics: "#7C3AED",
    chemistry: "#059669",
    biology: "#DC2626",
    english: "#B45309",
    superpowers: "#F59E0B",
    sst: "#0891B2",
    hindi: "#6B7280",
  },

  course: {
    maths: "#1E40AF",
    science: "#E8F0E8",
    english: "#92400E",
    sst: "#374151",
    hindi: "#6B7280",
    jeePhysics: "#065F46",
    jeeMaths: "#4C1D95",
    neetBio: "#7C3AED",
  },
};

// Theme-specific colors
export const LIGHT_THEME = {
  darkText: "#111827",
  midText: "#4B5563",
  mutedText: "#9CA3AF",
  white: "#FFFFFF",
  bg: "#FAFAFA",
  cream: "#FFF5F0",
  border: "#E5E7EB",
  cardBg: "rgba(255,255,255,0.75)",
  overlayCard: "rgba(255,248,244,0.88)",
  glassBg: "rgba(255,255,255,0.72)",
  bgGradient: "linear-gradient(135deg,#FFF5F0 0%,#FDE8D8 50%,#FFF0E8 100%)",
  heroGradient:
    "radial-gradient(ellipse 90% 70% at 55% 110%,#FFCFBC 0%,#FFE8DA 35%,#FFF8F4 75%,#FFFFFF 100%)",
};

export const DARK_THEME = {
  darkText: "#F9FAFB",
  midText: "#D1D5DB",
  mutedText: "#9CA3AF",
  white: "#111827",
  bg: "#111827",
  cream: "#111827",
  border: "#374151",
  cardBg: "rgba(31, 41, 55, 0.85)",
  overlayCard: "rgba(31, 41, 55, 0.9)",
  glassBg: "rgba(31, 41, 55, 0.8)",
  bgGradient: "linear-gradient(135deg,#111827 0%,#111827 50%,#111827 100%)",
  heroGradient:
    "radial-gradient(ellipse 90% 70% at 55% 110%,#374151 0%,#111827 35%,#111827 75%,#111827 100%)",
};

// Default export for backward compatibility (uses light theme)
export const C = { ...C_BASE, ...LIGHT_THEME };

export const FONT_HEAD = "'Plus Jakarta Sans','Nunito',sans-serif";
export const FONT_BODY = "'Plus Jakarta Sans','Nunito',sans-serif";

export const TYPE = {
  heroHeading: { fontSize: "clamp(2rem,4vw,3.75rem)", fontWeight: 800 },
  sectionHeading: { fontSize: "clamp(1.6rem,3vw,2.5rem)", fontWeight: 800 },
  cardTitle: { fontSize: "clamp(0.85rem,1vw,1rem)", fontWeight: 700 },
  body: { fontSize: "clamp(0.82rem,0.95vw,0.94rem)", fontWeight: 400 },
  caption: { fontSize: "clamp(0.65rem,0.8vw,0.72rem)", fontWeight: 500 },
  navLink: { fontSize: "clamp(0.8rem,0.9vw,0.875rem)", fontWeight: 600 },
};

const readIsDark = (): boolean =>
  typeof document !== "undefined" &&
  document.documentElement.classList.contains("dark");

let darkModeListeners: Array<() => void> = [];
let darkModeObserver: MutationObserver | null = null;

const subscribeDarkMode = (onChange: () => void): (() => void) => {
  darkModeListeners.push(onChange);
  if (!darkModeObserver && typeof MutationObserver !== "undefined") {
    darkModeObserver = new MutationObserver(() => {
      darkModeListeners.forEach((fn) => fn());
    });
    darkModeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }
  return () => {
    darkModeListeners = darkModeListeners.filter((fn) => fn !== onChange);
    if (darkModeListeners.length === 0 && darkModeObserver) {
      darkModeObserver.disconnect();
      darkModeObserver = null;
    }
  };
};

const LIGHT_COLORS = { ...C_BASE, ...LIGHT_THEME };
const DARK_COLORS = { ...C_BASE, ...DARK_THEME };

// Helper hook to get current theme colors
export const useThemeColors = () => {
  const isDark = useSyncExternalStore(
    subscribeDarkMode,
    readIsDark,
    () => false,
  );
  return isDark ? DARK_COLORS : LIGHT_COLORS;
};
